/// frontend/src/pages/Contact.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faPhone,
  faEnvelope,
  faClock,
  faPaperPlane,
  faCheckCircle,
  faSpinner,
  faUser,
  faBuilding,
  faComment,
  faArrowRight,
  faExclamationTriangle,
  faWhatsapp,
  faInfoCircle,
  faMinus,
  faPlus,
  faStore,
  faTractor,
  faHandshake,
  faTruck,
  faMoneyBillWave,
  faQuestionCircle,
  faHeadset,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import { createContactMessage } from "../api";
import { useAuth } from "../context/AuthContext";
import SEO from "../components/SEO/SEO";
import "../styles/contact.scss";

// ============================================
// CONSTANTS
// ============================================
const CONTACT_INFO = [
  {
    icon: faMapMarkerAlt,
    title: "Visit Us",
    details: ["30 Kissy Road, Freetown, Sierra Leone"],
    color: "#006b2e",
  },
  {
    icon: faPhone,
    title: "Call Us",
    details: ["+232 31 030808", "+1 949 267 8678"],
    color: "#d9a321",
  },
  {
    icon: faEnvelope,
    title: "Email Us",
    details: ["kenalienterprisesl@gmail.com"],
    color: "#3b82f6",
  },
  {
    icon: faWhatsappBrand,
    title: "WhatsApp",
    details: ["+232 31 030808"],
    color: "#25d366",
  },
  {
    icon: faClock,
    title: "Business Hours",
    details: [
      "Monday - Friday: 8:00 AM - 6:00 PM",
      "Saturday: 9:00 AM - 2:00 PM",
      "Sunday: Closed",
    ],
    color: "#6b7280",
  },
];

const CATEGORIES = [
  { value: "general", label: "General Inquiry", icon: faInfoCircle },
  { value: "sales", label: "Sales Inquiry", icon: faStore },
  { value: "support", label: "Customer Support", icon: faHeadset },
  { value: "partnership", label: "Partnership", icon: faHandshake },
  { value: "supplier", label: "Supplier Registration", icon: faGlobe },
  { value: "farmer", label: "Farmer Support", icon: faTractor },
];

const FAQS = [
  {
    q: "What products does Kenali Enterprise offer?",
    a: "We offer premium livestock nutrition products including Rumen Bypass Fat (GrowPro™), DairyBoost™ dairy supplement, Mineralite™ mineral salt blocks, and protein concentrates. All products are sourced from trusted manufacturers in India.",
  },
  {
    q: "Do you ship across Sierra Leone?",
    a: "Yes, we deliver to farmers across all districts in Sierra Leone, including Freetown, Makeni, Bo, Kenema, Koidu, and other major towns. We offer both bulk and small-package delivery options.",
  },
  {
    q: "What are your payment options?",
    a: "We accept Cash on Delivery, Bank Transfers, Mobile Money (Orange Money, Afrimoney, WAVE), and Credit terms for approved farmers. We also offer deposit options for bulk orders.",
  },
  {
    q: "How can I become a distributor or reseller?",
    a: "We're always looking for reliable distribution partners. Contact us to discuss distributor opportunities, wholesale pricing, and partnership terms. We offer competitive margins for resellers.",
  },
  {
    q: "What is the minimum order quantity?",
    a: "For retail customers, the minimum order is 2kg. For farmers and bulk buyers, we offer 25kg, 50kg, and 100kg+ options. Bulk orders receive significant discounts.",
  },
  {
    q: "How do I track my order?",
    a: "You can track your order using the tracking number provided in your confirmation email. Visit our order tracking page on the website or contact our support team for assistance.",
  },
];

// ============================================
// MAIN COMPONENT
// ============================================
const Contact = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    category: "general",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [successData, setSuccessData] = useState(null);
  const [activeFaq, setActiveFaq] = useState(null);

  // Auto-fill form if user is logged in
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    if (!formData.name.trim()) {
      setError("Please enter your full name");
      setSubmitting(false);
      return;
    }
    if (!formData.email.trim() || !formData.email.includes("@")) {
      setError("Please enter a valid email address");
      setSubmitting(false);
      return;
    }
    if (!formData.message.trim() || formData.message.length < 10) {
      setError("Please enter a message (minimum 10 characters)");
      setSubmitting(false);
      return;
    }

    try {
      const response = await createContactMessage({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || "",
        subject: formData.subject || "General Inquiry",
        message: formData.message,
        category: formData.category,
        source: "website",
      });

      setSuccessData(response.data);
      setSubmitted(true);
      setFormData((prev) => ({
        ...prev,
        name: user?.name || "",
        email: user?.email || "",
        phone: user?.phone || "",
        subject: "",
        message: "",
        category: "general",
      }));
    } catch (err) {
      console.error("Contact form error:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Something went wrong. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <>
      <SEO
        title="Contact Kenali Enterprise - Livestock Feed & Animal Nutrition"
        description="Contact Kenali Enterprise for premium livestock feed supplements, animal nutrition products, and farming solutions in Sierra Leone."
        keywords="contact Kenali Enterprise, livestock feed Sierra Leone, animal nutrition, farming solutions"
        canonical="https://kenalienterprise.com/contact"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="contact-page"
      >
        {/* Hero Section */}
        <section className="page-hero">
          <div className="page-hero-overlay" />
          <div className="container page-hero-content">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <FontAwesomeIcon icon={faEnvelope} className="page-hero-icon" />
              <h1 className="page-hero-title">Contact Us</h1>
              <p className="page-hero-subtitle">
                Have questions about our products or want to place an order?
                Reach out to us today. We're here to help you find the right
                nutrition solutions for your livestock.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="contact-section">
          <div className="container">
            <div className="contact-grid">
              {/* Contact Form */}
              <motion.div
                initial={{ x: -30, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="contact-form-wrapper"
              >
                <div className="form-header">
                  <h2 className="contact-form-title">
                    <FontAwesomeIcon icon={faPaperPlane} />
                    Send Us a Message
                  </h2>
                  <p className="contact-form-subtitle">
                    We'll get back to you within 24 hours.
                    {user && (
                      <span className="user-badge">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        Logged in as {user.name}
                      </span>
                    )}
                  </p>
                </div>

                {submitted ? (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="success-message"
                  >
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="success-icon"
                    />
                    <h3>Message Sent Successfully!</h3>
                    <p>
                      Thank you for reaching out,{" "}
                      <strong>{formData.name || "valued customer"}</strong>.
                      We'll respond shortly.
                    </p>
                    {successData?.data?._id && (
                      <p className="reference-number">
                        Reference: #{successData.data._id.slice(-8)}
                      </p>
                    )}
                    <div className="success-actions">
                      <button
                        className="btn btn-outline"
                        onClick={() => {
                          setSubmitted(false);
                          setSuccessData(null);
                        }}
                      >
                        Send Another Message
                      </button>
                      <Link to="/products" className="btn btn-primary">
                        Browse Products
                      </Link>
                    </div>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="contact-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>
                          <FontAwesomeIcon icon={faUser} /> Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Enter your full name"
                          className={error && !formData.name ? "error" : ""}
                        />
                      </div>
                      <div className="form-group">
                        <label>
                          <FontAwesomeIcon icon={faEnvelope} /> Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="you@example.com"
                          className={error && !formData.email ? "error" : ""}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>
                          <FontAwesomeIcon icon={faPhone} /> Phone Number
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+232 76 123456"
                        />
                      </div>
                      <div className="form-group">
                        <label>
                          <FontAwesomeIcon icon={faBuilding} /> Category
                        </label>
                        <select
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                        >
                          {CATEGORIES.map((cat) => (
                            <option key={cat.value} value={cat.value}>
                              <FontAwesomeIcon icon={cat.icon} /> {cat.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faComment} /> Subject
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="What's this about?"
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faComment} /> Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="5"
                        placeholder="Tell us how we can help you..."
                        className={error && !formData.message ? "error" : ""}
                      />
                      <div className="char-counter">
                        {formData.message.length} / 5000 characters
                      </div>
                    </div>

                    {error && (
                      <div className="error-message">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span>{error}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-primary btn-large btn-full"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin /> Sending...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faPaperPlane} /> Send Message
                        </>
                      )}
                    </button>

                    <p className="form-footer">
                      <FontAwesomeIcon icon={faCheckCircle} />
                      We respect your privacy. Your information is secure.
                    </p>
                  </form>
                )}
              </motion.div>

              {/* Contact Info */}
              <motion.div
                initial={{ x: 30, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="contact-info-wrapper"
              >
                <h2 className="contact-info-title">
                  <FontAwesomeIcon icon={faHeadset} />
                  Get in Touch
                </h2>
                <p className="contact-info-subtitle">
                  We're here to answer your questions and help you find the
                  right nutrition solutions for your livestock.
                </p>

                <div className="contact-info-list">
                  {CONTACT_INFO.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ y: 20, opacity: 0 }}
                      whileInView={{ y: 0, opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="contact-info-item"
                    >
                      <div
                        className="contact-info-icon"
                        style={{
                          background: `${item.color}15`,
                          color: item.color,
                        }}
                      >
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <div className="contact-info-details">
                        <h4>{item.title}</h4>
                        {item.details.map((detail, i) => (
                          <p key={i}>{detail}</p>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Quick Contact Buttons */}
                <div className="quick-contact">
                  <a
                    href="https://wa.me/23231030808"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="quick-contact-btn whatsapp"
                  >
                    <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp Us
                  </a>
                  <a href="tel:+23231030808" className="quick-contact-btn call">
                    <FontAwesomeIcon icon={faPhone} /> Call Us
                  </a>
                  <a
                    href="mailto:kenalienterprisesl@gmail.com"
                    className="quick-contact-btn email"
                  >
                    <FontAwesomeIcon icon={faEnvelope} /> Email Us
                  </a>
                </div>

                {/* Map Placeholder */}
                <div className="map-placeholder">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="map-icon" />
                  <span>30 Kissy Road, Freetown, Sierra Leone</span>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <div className="container">
            <div className="section-header">
              <span className="section-tag">
                <FontAwesomeIcon icon={faQuestionCircle} /> FAQ
              </span>
              <h2>Frequently Asked Questions</h2>
              <p className="section-subtitle">
                Quick answers to the most common questions from our customers
              </p>
            </div>
            <div className="faq-grid">
              {FAQS.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`faq-card ${activeFaq === index ? "active" : ""}`}
                  onClick={() => toggleFaq(index)}
                >
                  <div className="faq-header">
                    <h4>
                      <FontAwesomeIcon icon={faQuestionCircle} />
                      {faq.q}
                    </h4>
                    <span className="faq-toggle">
                      <FontAwesomeIcon icon={activeFaq === index ? faMinus : faPlus} />
                    </span>
                  </div>
                  {activeFaq === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      transition={{ duration: 0.3 }}
                      className="faq-answer"
                    >
                      <p>{faq.a}</p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="contact-cta">
          <div className="container">
            <div className="cta-content">
              <div className="cta-badge">
                <FontAwesomeIcon icon={faHandshake} /> Partner With Us
              </div>
              <h2>Ready to Work with Kenali Enterprise?</h2>
              <p>Let's find the right nutrition solution for your livestock.</p>
              <div className="cta-buttons">
                <Link to="/products" className="btn btn-primary btn-large">
                  Explore Products <FontAwesomeIcon icon={faArrowRight} />
                </Link>
                <Link to="/register" className="btn btn-outline-white btn-large">
                  Register as Farmer
                </Link>
              </div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default Contact;;
